import { addDays, format, isValid, parseISO, startOfDay } from "date-fns";

export type NotificationsTimeRange = "all" | "today" | "7d" | "30d" | "custom";

/** A user-picked span of whole local calendar days, both ends inclusive, as
 *  `yyyy-MM-dd`. Only meaningful while `timeRange === "custom"`. */
export interface NotificationsCustomRange {
  from: string;
  to: string;
}

/** The time-range half of the Notifications filter. The store keeps this, not
 *  computed bounds, so every read resolves "Today" / "Last 7 days" against the
 *  clock at that moment — a tab left open overnight must not keep querying
 *  yesterday's window. */
export interface NotificationsTimeRangeSelection {
  timeRange: NotificationsTimeRange;
  customRange: NotificationsCustomRange | null;
}

/** The custom range's days in order, or null when there is none or either
 *  end does not parse. One reading for both the chip and the query, so the
 *  screen can never show a range the feed is not filtered by. */
const DAY_SHAPE = /^\d{4}-\d{2}-\d{2}$/;

function customRangeDays(range: NotificationsCustomRange | null): [Date, Date] | null {
  if (!range) return null;
  // parseISO accepts "2026-10" or "2026" as the first of the month / year;
  // a truncated value is malformed, not a bound.
  if (!DAY_SHAPE.test(range.from) || !DAY_SHAPE.test(range.to)) return null;
  const a = parseISO(range.from);
  const b = parseISO(range.to);
  if (!isValid(a) || !isValid(b)) return null;
  return a <= b ? [a, b] : [b, a];
}

/** The custom range the feed is filtered by, in order, or null when the
 *  selection is not a usable custom range. What the UI labels and seeds the
 *  calendar from, so it cannot read the range differently from the query. */
export function appliedCustomRange(
  v: NotificationsTimeRangeSelection
): NotificationsCustomRange | null {
  if (v.timeRange !== "custom") return null;
  const days = customRangeDays(v.customRange);
  return days ? { from: format(days[0], "yyyy-MM-dd"), to: format(days[1], "yyyy-MM-dd") } : null;
}

/** Whether the selection restricts anything. "custom" without a usable range
 *  does not — so it shows no chip and does not count as active. */
export function hasTimeRangeFilter(v: NotificationsTimeRangeSelection): boolean {
  return v.timeRange === "custom" ? customRangeDays(v.customRange) !== null : v.timeRange !== "all";
}

/** Translate the time-range selection to `[since, until)` ISO bounds.
 *
 *  Presets are N local calendar days including today ("Last 7 days" = today
 *  and the six before it, from midnight), open-ended so new activity keeps
 *  arriving — the same convention as the Documents "Added" filter's
 *  `recentAddedRange`, mirrored rather than imported so the PM store does not
 *  depend on a Documents component module. A custom range covers whole local
 *  days, `until` being the start of the day after the last one picked; an
 *  unparseable range is no restriction, and a reversed one is put in order. */
export function timeRangeBounds(
  filter: NotificationsTimeRangeSelection,
  now = new Date()
): { since?: string; until?: string } {
  const range = filter.timeRange;
  if (range === "all") return {};
  if (range === "custom") {
    const days = customRangeDays(filter.customRange);
    if (!days) return {};
    const [first, last] = days;
    return {
      since: startOfDay(first).toISOString(),
      until: startOfDay(addDays(last, 1)).toISOString(),
    };
  }
  const days = range === "today" ? 1 : range === "7d" ? 7 : 30;
  return { since: startOfDay(addDays(now, -(days - 1))).toISOString() };
}
